"use client";

import posthog from "posthog-js";
import { CircleAlert, RefreshCw, type IconComponent } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { currentBuildKey, reloadServingBuild } from "@/lib/chunk-load-recovery";
import { cn } from "@/lib/utils";

type NoticeSize = "compact" | "page";

export interface ReloadNoticeLabel {
  title: string;
  icon?: IconComponent;
}

interface ReloadNoticeProps {
  title?: string;
  icon?: IconComponent;
  size?: NoticeSize;
  action?: { label: string; onClick: () => void };
  navigation?: { label: string; href: string };
  className?: string;
}

function reloadFromNotice(): void {
  posthog.capture("chunk_load_reload_clicked", {
    bundle_release: currentBuildKey(),
    pathname: window.location.pathname,
  });
  reloadServingBuild();
}

function ReloadNoticeHeading({
  title,
  icon: Icon,
  size,
}: {
  title: string;
  icon: IconComponent;
  size: NoticeSize;
}) {
  const isPage = size === "page";
  const Title = isPage ? "h1" : "p";
  return (
    <div className="flex flex-col items-center gap-3">
      <div
        className={cn(
          "flex items-center justify-center rounded-full bg-muted",
          isPage ? "size-12" : "size-10",
        )}
      >
        <Icon
          aria-hidden="true"
          className={cn("text-muted-foreground", isPage ? "size-6" : "size-5")}
        />
      </div>
      <Title
        className={cn(
          "text-balance font-semibold text-foreground",
          isPage ? "text-xl" : "text-sm",
        )}
      >
        {title}
      </Title>
    </div>
  );
}

function ReloadNoticeActions({
  size,
  action,
  navigation,
}: Pick<ReloadNoticeProps, "action" | "navigation"> & { size: NoticeSize }) {
  const isPage = size === "page";
  return (
    <div
      className={cn(
        "flex w-full flex-col gap-2",
        isPage ? "max-w-xs" : "max-w-48",
      )}
    >
      <Button
        type="button"
        variant={isPage ? "primary" : "outline"}
        size={isPage ? "default" : "sm"}
        onClick={action?.onClick ?? reloadFromNotice}
      >
        <RefreshCw className="size-4" />
        {action?.label ?? "Reload"}
      </Button>
      {navigation ? (
        <Button variant="outline" asChild>
          <a href={navigation.href}>{navigation.label}</a>
        </Button>
      ) : null}
    </div>
  );
}

export function ReloadNotice({
  title,
  icon = CircleAlert,
  size = "compact",
  action,
  navigation,
  className,
}: ReloadNoticeProps) {
  return (
    <div
      role="alert"
      className={cn(
        "flex h-full w-full flex-col items-center justify-center text-center",
        size === "page" ? "gap-5" : "gap-4 p-6",
        className,
      )}
    >
      <ReloadNoticeHeading
        title={
          title ??
          (size === "page"
            ? "This page didn't load"
            : "Part of this page didn't load")
        }
        icon={icon}
        size={size}
      />
      <ReloadNoticeActions
        size={size}
        action={action}
        navigation={navigation}
      />
    </div>
  );
}
